import * as z from "zod";
import { flag, pagination } from "../index";
import { hubspotReadingDecisions, hubspotReadingStatuses, hubspotRecordKinds, hubspotWaitingReasons } from "./enums";

// The records of HubSpot as party keeps them (50-api/party.md §2.10; I-28, ADR-109, ADR-110, ADR-116): the latest
// state of each, the entity of Nexxt it became, or why it waits. A record enters by itself when what it waits for
// arrives, but a company whose VAT number finds an organization that is not a customer, which a person confirms
// (ADR-191). The full readings of HubSpot, and the ones that wait for a person before archiving (ADR-191).

const hubspotRecordFilters = {
  kind: z.enum(hubspotRecordKinds).optional(),
  /** Only the ones that wait, with any reason: the queue of who looks at them. */
  waiting: flag.optional(),
  waitingReason: z.enum(hubspotWaitingReasons).optional(),
  /** Only the ones HubSpot no longer has, instead of the others. */
  archived: flag.optional(),
  /** Part of the name, of the email or of the HubSpot id, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
};

/** The latest changed first. */
export const listHubspotRecordsInput = z.object({
  ...hubspotRecordFilters,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListHubspotRecordsInput = z.input<typeof listHubspotRecordsInput>;

/** How many records the list has with the same filters, and since when the oldest waits (ADR-086). */
export const countHubspotRecordsInput = z.object(hubspotRecordFilters);
export type CountHubspotRecordsInput = z.input<typeof countHubspotRecordsInput>;

export const hubspotRecordDto = z
  .object({
    id: z.uuid(),
    kind: z.enum(hubspotRecordKinds),
    hubspotId: z.string(),
    /** What HubSpot calls the record: its name, or the name of a person, or its email. */
    label: z.string(),
    /** What tells it apart besides the name: an email, a customer code, a VAT number. */
    detail: z.string().nullable().optional(),
    /** The entity of Nexxt it became: an organization, a user, a buying group; none for a contact, which is many. */
    entity: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    waitingReason: z.enum(hubspotWaitingReasons).nullable(),
    /** With `not_customer`: the organization with the VAT number of the company, which a person confirms or not. */
    proposal: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    /** `updatedAt` of the state kept, as HubSpot gives it. */
    hubspotUpdatedAt: z.iso.datetime(),
    /** When a reading last named it. */
    seenAt: z.iso.datetime(),
    /** When a reading said HubSpot no longer has it. */
    archivedAt: z.iso.datetime().nullable(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "HubspotRecord" });

/**
 * A person decides a company that waits with `not_customer` (ADR-191): `organizationId` is the organization proposed,
 * which becomes its customer; null, the company is not that organization, and a new customer is born.
 */
export const confirmHubspotCompanyInput = z.object({ recordId: z.uuid(), organizationId: z.uuid().nullable() });
export type ConfirmHubspotCompanyInput = z.input<typeof confirmHubspotCompanyInput>;
export const hubspotRecordParams = confirmHubspotCompanyInput.pick({ recordId: true });
export const confirmHubspotCompanyBody = confirmHubspotCompanyInput.omit({ recordId: true });

// --- Full readings (ADR-109, ADR-191) ---

const hubspotReadingFilters = {
  kind: z.enum(hubspotRecordKinds).optional(),
  status: z.enum(hubspotReadingStatuses).optional(),
  /** Only the ones that wait for a person: held, or incomplete. */
  waiting: flag.optional(),
};

/** The latest read first; with `waiting`, the oldest first. */
export const listHubspotReadingsInput = z.object({
  ...hubspotReadingFilters,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListHubspotReadingsInput = z.input<typeof listHubspotReadingsInput>;

/** How many readings the list has with the same filters, and since when the oldest waits (ADR-086). */
export const countHubspotReadingsInput = z.object(hubspotReadingFilters);
export type CountHubspotReadingsInput = z.input<typeof countHubspotReadingsInput>;

export const hubspotReadingDto = z
  .object({
    id: z.uuid(),
    kind: z.enum(hubspotRecordKinds),
    publicationId: z.string(),
    status: z.enum(hubspotReadingStatuses),
    /** How many parts the reading has, and how many arrived. */
    partCount: z.number().int(),
    partsArrived: z.number().int(),
    /** The earliest `readAt` of its parts: a record seen before it, and not named, is what it archives. */
    readAt: z.iso.datetime(),
    /** Once complete: the records of the kind HubSpot had, and the ones the reading does not name. */
    recordCount: z.number().int().nullable(),
    unseenCount: z.number().int().nullable(),
    /** When it was held for a person, and when it was completed or discarded. */
    heldAt: z.iso.datetime().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "HubspotReading" });

/**
 * A person decides a reading that waits (ADR-191): `release` archives what a held reading does not name, as if it had
 * been within the limit; `discard` archives nothing, for a held or an incomplete reading.
 */
export const decideHubspotReadingInput = z.object({
  readingId: z.uuid(),
  decision: z.enum(hubspotReadingDecisions),
});
export type DecideHubspotReadingInput = z.input<typeof decideHubspotReadingInput>;
export const hubspotReadingParams = decideHubspotReadingInput.pick({ readingId: true });
export const decideHubspotReadingBody = decideHubspotReadingInput.omit({ readingId: true });
